import { c as classNames, o as useNavigation, e as useLocation, f as useTypedSelector, p as selectIsLoggedIn, dq as Outlet, aq as Navigate, d as useContext, dr as useModuleContextProvider, F as __awaiter, ds as selectAuthError, K as selectCurrentUser, u as useStrings, dt as getResponsiveValue, bt as mergeRefs, a8 as Link, U as FontAwesomeIcon, ai as createClassName, q as queryString, bX as matchPath } from './className-0524835a.js';
import React__default, { forwardRef, useRef, useMemo, useCallback, useState, useEffect } from 'react';
import { u as useElementWidth, b as Skeleton, a as SkeletonContainer, S as Skeleton$1, i as isNumeric } from './SkeletonContainer-fd90b3af.js';
import { T as Text } from './Text-45e81862.js';

const Centred = props => (React__default.createElement("div", { className: "Centred" }, props.children));

const CentredMessage = ({ title, description }) => (React__default.createElement(Centred, null,
    React__default.createElement("div", { className: "CentredMessage" },
        React__default.createElement("h2", null, title),
        description && React__default.createElement(Text, { element: "p" }, description))));

const Screen = forwardRef((props, ref) => {
    const classes = classNames('Screen', props.className, {
        'Screen-noHeader': !props.header
    });
    function getContent() {
        if (props.errorText) {
            return React__default.createElement(CentredMessage, { title: props.errorText });
        }
        return props.children;
    }
    return React__default.createElement("div", { ref: ref, className: classes },
        props.header,
        React__default.createElement("div", { className: "Screen-content", ref: props.contentRef },
            getContent(),
            props.bottomPagination));
});
Screen.displayName = 'Screen';

const RouteAccess = ({ access, children }) => {
    const navigation = useNavigation();
    const location = useLocation();
    const redirectUrl = useRef(navigation.login(location));
    const isLoggedIn = useTypedSelector(selectIsLoggedIn);
    if (canAccess(access, isLoggedIn)) {
        return children ? React__default.createElement(React__default.Fragment, null, children) : React__default.createElement(Outlet, null);
    }
    else if (access === 'guests') {
        return React__default.createElement(Navigate, { to: navigation.home(), replace: true });
    }
    else {
        return React__default.createElement(Navigate, { to: redirectUrl.current, replace: true });
    }
};
function canAccess(access, isLoggedIn) {
    return !access || access === 'all' || (access === 'users' && isLoggedIn) || (access === 'guests' && !isLoggedIn);
}

function useModuleHooks() {
    const { modules } = useContext();
    const getModuleContext = useModuleContextProvider();
    return useMemo(() => {
        var _a;
        return (_a = modules === null || modules === void 0 ? void 0 : modules.flatMap(mod => { var _a, _b; return (_b = (_a = mod.hooks) === null || _a === void 0 ? void 0 : _a.call(mod, getModuleContext(mod.id))) !== null && _b !== void 0 ? _b : []; })) !== null && _a !== void 0 ? _a : [];
    }, [modules, getModuleContext]);
}
function useModuleHeaderItems() {
    const hooks = useModuleHooks();
    return useMemo(() => {
        return hooks.flatMap(hook => { var _a, _b; return (_b = (_a = hook.headerItems) === null || _a === void 0 ? void 0 : _a.call(hook)) !== null && _b !== void 0 ? _b : []; });
    }, [hooks]);
}
function useModuleAccountMenuItems() {
    const hooks = useModuleHooks();
    const [items, setItems] = useState([]);
    useEffect(() => {
        let updateItems = true;
        Promise.allSettled(hooks.flatMap(hook => { var _a, _b; return (_b = (_a = hook.accountMenuItems) === null || _a === void 0 ? void 0 : _a.call(hook)) !== null && _b !== void 0 ? _b : []; }))
            .then(results => results.flatMap(result => result.status === 'fulfilled' ? result.value : []))
            .then(items => {
            if (updateItems) {
                setItems(items);
            }
        });
        return () => {
            updateItems = false;
        };
    }, [hooks]);
    return items;
}
function useModuleSidebarItems() {
    const hooks = useModuleHooks();
    const [items, setItems] = useState([]);
    useEffect(() => {
        let updateItems = true;
        Promise.allSettled(hooks.flatMap(hook => { var _a, _b; return (_b = (_a = hook.sidebarItems) === null || _a === void 0 ? void 0 : _a.call(hook)) !== null && _b !== void 0 ? _b : []; }))
            .then(results => results.flatMap(result => result.status === 'fulfilled' ? result.value : []))
            .then(items => {
            if (updateItems) {
                setItems(items);
            }
        });
        return () => {
            updateItems = false;
        };
    }, [hooks]);
    return items;
}
function useModuleVehicleSidebarItems(vehicle) {
    const hooks = useModuleHooks();
    const [items, setItems] = useState([]);
    useEffect(() => {
        let updateItems = true;
        if (vehicle) {
            Promise.allSettled(hooks.flatMap(hook => { var _a, _b; return (_b = (_a = hook.vehicleSidebarItems) === null || _a === void 0 ? void 0 : _a.call(hook, vehicle)) !== null && _b !== void 0 ? _b : []; }))
                .then(results => results.flatMap(result => result.status === 'fulfilled' ? result.value : []))
                .then(items => {
                if (updateItems) {
                    setItems(items);
                }
            });
        }
        else {
            setItems([]);
        }
        return () => {
            updateItems = false;
        };
    }, [hooks, vehicle]);
    return items;
}
function useModuleVehicleTabs() {
    const { modules } = useContext();
    const isLoggedIn = useTypedSelector(selectIsLoggedIn);
    return useMemo(() => {
        var _a;
        const tabs = (_a = modules === null || modules === void 0 ? void 0 : modules.flatMap(({ layouts }) => { var _a; return (_a = layouts === null || layouts === void 0 ? void 0 : layouts.vehicleTabs) !== null && _a !== void 0 ? _a : []; })) !== null && _a !== void 0 ? _a : [];
        return tabs.filter(result => canAccess(result.visibility, isLoggedIn));
    }, [isLoggedIn, modules]);
}
function useLegacyModuleVehicleTabs(vehicle) {
    const hooks = useModuleHooks();
    const [items, setItems] = useState(null);
    const isLoggedIn = useTypedSelector(selectIsLoggedIn);
    useEffect(() => {
        let updateItems = true;
        if (vehicle) {
            Promise.allSettled(hooks.flatMap(hook => { var _a, _b; return (_b = (_a = hook.vehicleTabs) === null || _a === void 0 ? void 0 : _a.call(hook, vehicle)) !== null && _b !== void 0 ? _b : []; }))
                .then(results => results.flatMap(result => result.status === 'fulfilled' ? result.value : []))
                .then(results => results.filter(result => canAccess(result.visibility, isLoggedIn)))
                .then(items => {
                if (updateItems) {
                    setItems(items);
                }
            });
        }
        else {
            setItems([]);
        }
        return () => {
            updateItems = false;
        };
    }, [hooks, vehicle, isLoggedIn]);
    return items;
}
function useModuleVehicleInformation(vehicle, items) {
    const hooks = useModuleHooks();
    return useMemo(() => {
        return hooks.reduce((items, hook) => {
            var _a, _b;
            return (_b = (_a = hook.vehicleInformation) === null || _a === void 0 ? void 0 : _a.call(hook, vehicle, items)) !== null && _b !== void 0 ? _b : items;
        }, items);
    }, [hooks, items, vehicle]);
}
function useModuleDocumentActions(document) {
    const hooks = useModuleHooks();
    return useMemo(() => {
        return hooks.flatMap(hook => { var _a, _b; return (_b = (_a = hook.documentActions) === null || _a === void 0 ? void 0 : _a.call(hook, document)) !== null && _b !== void 0 ? _b : []; });
    }, [hooks, document]);
}
function useModuleDocumentCoverPages() {
    const hooks = useModuleHooks();
    return useCallback((document) => __awaiter(this, void 0, void 0, function* () {
        const pages = yield Promise.allSettled(hooks.flatMap(hook => { var _a, _b; return (_b = (_a = hook.documentCoverPage) === null || _a === void 0 ? void 0 : _a.call(hook, document)) !== null && _b !== void 0 ? _b : []; }));
        return pages.filter((page) => {
            return (page.status === 'fulfilled' && page.value !== null) || page.status === 'rejected';
        });
    }), [hooks]);
}
function useModuleDocumentMessages(document) {
    const hooks = useModuleHooks();
    return useCallback(message => {
        return hooks.find(hook => { var _a; return (_a = hook.documentMessage) === null || _a === void 0 ? void 0 : _a.call(hook, document, message); }) !== undefined;
    }, [hooks, document]);
}
function useModuleRelayState() {
    const hooks = useModuleHooks();
    return useCallback((relayState) => __awaiter(this, void 0, void 0, function* () {
        const relayStateHooks = hooks.flatMap(hook => { var _a, _b; return (_b = (_a = hook.relayState) === null || _a === void 0 ? void 0 : _a.call(hook, relayState)) !== null && _b !== void 0 ? _b : []; });
        return Promise.allSettled(relayStateHooks).then(getFirstFulfilledNonNullResult);
    }), [hooks]);
}
function useModuleDealerNameAsync() {
    const hooks = useModuleHooks();
    const user = useTypedSelector(selectCurrentUser);
    return useCallback(() => __awaiter(this, void 0, void 0, function* () {
        if (!user) {
            return null;
        }
        const dealerNameHooks = hooks.flatMap(hook => { var _a, _b; return (_b = (_a = hook.dealerName) === null || _a === void 0 ? void 0 : _a.call(hook, user)) !== null && _b !== void 0 ? _b : []; });
        return Promise.allSettled(dealerNameHooks).then(getFirstFulfilledNonNullResult);
    }), [hooks, user]);
}
function useModuleDealerName() {
    const getDealerName = useModuleDealerNameAsync();
    const [dealerName, setDealerName] = useState(null);
    useEffect(() => {
        getDealerName().then(setDealerName);
    }, [getDealerName]);
    return dealerName;
}
function useModuleLogOut() {
    const hooks = useModuleHooks();
    return useCallback((logOutUrl) => __awaiter(this, void 0, void 0, function* () {
        yield Promise.all(hooks.flatMap(hook => { var _a, _b; return (_b = (_a = hook.logOut) === null || _a === void 0 ? void 0 : _a.call(hook, logOutUrl)) !== null && _b !== void 0 ? _b : []; }));
    }), [hooks]);
}
function useModuleSsoConfig() {
    const hooks = useModuleHooks();
    return useMemo(() => { var _a, _b, _c; return (_c = (_b = (_a = hooks.find(hook => hook.ssoConfig)) === null || _a === void 0 ? void 0 : _a.ssoConfig) === null || _b === void 0 ? void 0 : _b.call(_a)) !== null && _c !== void 0 ? _c : null; }, [hooks]);
}
function useModuleVehicleChanged() {
    const hooks = useModuleHooks();
    return useCallback((vehicle) => __awaiter(this, void 0, void 0, function* () {
        yield Promise.all(hooks.flatMap(hook => { var _a, _b; return (_b = (_a = hook.vehicleChanged) === null || _a === void 0 ? void 0 : _a.call(hook, vehicle)) !== null && _b !== void 0 ? _b : []; }));
    }), [hooks]);
}
function useModuleAuthRevokeStrategy() {
    const hooks = useModuleHooks();
    const authError = useTypedSelector(selectAuthError);
    return useCallback(() => __awaiter(this, void 0, void 0, function* () {
        const authRevokedStrategyHooks = hooks.flatMap(hook => { var _a, _b; return (_b = (_a = hook.authRevokeStrategy) === null || _a === void 0 ? void 0 : _a.call(hook, authError)) !== null && _b !== void 0 ? _b : []; });
        return Promise.allSettled(authRevokedStrategyHooks).then(getFirstFulfilledNonNullResult);
    }), [hooks, authError]);
}
function getFirstFulfilledNonNullResult(results) {
    for (const result of results) {
        if (result.status === 'fulfilled' && result.value !== null) {
            return result.value;
        }
    }
    return null;
}

var faArrowLeft = {};

var hasRequiredFaArrowLeft;

function requireFaArrowLeft () {
	if (hasRequiredFaArrowLeft) return faArrowLeft;
	hasRequiredFaArrowLeft = 1;
	(function (exports) {
		Object.defineProperty(exports, '__esModule', { value: true });
		var prefix = 'fas';
		var iconName = 'arrow-left';
		var width = 448;
		var height = 512;
		var ligatures = [];
		var unicode = 'f060';
		var svgPathData = 'M257.5 445.1l-22.2 22.2c-9.4 9.4-24.6 9.4-33.9 0L7 273c-9.4-9.4-9.4-24.6 0-33.9L201.4 44.7c9.4-9.4 24.6-9.4 33.9 0l22.2 22.2c9.5 9.5 9.3 25-.4 34.3L136.6 216H424c13.3 0 24 10.7 24 24v32c0 13.3-10.7 24-24 24H136.6l120.5 114.8c9.8 9.3 10 24.8.4 34.3z';

		exports.definition = {
		  prefix: prefix,
		  iconName: iconName,
		  icon: [
		    width,
		    height,
		    ligatures,
		    unicode,
		    svgPathData
		  ]};

		exports.faArrowLeft = exports.definition;
		exports.prefix = prefix;
		exports.iconName = iconName;
		exports.width = width;
		exports.height = height;
		exports.ligatures = ligatures;
		exports.unicode = unicode;
		exports.svgPathData = svgPathData;
} (faArrowLeft));
	return faArrowLeft;
}

var faArrowLeftExports = requireFaArrowLeft();

const loadingToken = Symbol('loadingToken');

const ContentHeader = props => {
    var _a;
    const { buttons: strings } = useStrings();
    const { ref: widthRef, width } = useElementWidth();
    const columns = getResponsiveValue(props.columns, width);
    const title = (_a = props.backTitle) !== null && _a !== void 0 ? _a : strings.back;
    const classes = classNames('ContentHeader', props.className, {
        'ContentHeader-twoColumns': columns === 2,
        'ContentHeader-threeColumns': columns === 3
    });
    return React__default.createElement("div", { className: classes, ref: mergeRefs(props.innerRef, widthRef) },
        props.backUrl && React__default.createElement(Link, { className: "ContentHeader-back", href: props.backUrl, title: title, ariaLabel: title },
            React__default.createElement(FontAwesomeIcon, { icon: faArrowLeftExports.faArrowLeft, size: "lg" })),
        React__default.createElement("div", { className: "ContentHeader-content" }, props.children));
};
const Title = ({ title, subtitle, adornment }) => {
    const isTitleLoading = title === loadingToken;
    const isSubtitleLoading = subtitle === loadingToken;
    return React__default.createElement("div", { className: "ContentHeaderTitle" },
        React__default.createElement("div", { className: "ContentHeaderTitle-title" },
            React__default.createElement("h2", { "aria-live": "polite", "aria-busy": isTitleLoading }, isTitleLoading ? React__default.createElement(Skeleton, { width: "40%", maxWidth: 250 }) : title),
            adornment && React__default.createElement("div", { className: "ContentHeaderTitle-adornment" }, adornment)),
        subtitle && React__default.createElement("div", { className: "ContentHeaderTitle-subtitle", "aria-live": "polite", "aria-busy": isSubtitleLoading, "data-testid": "subtitle" }, isSubtitleLoading ? React__default.createElement(Skeleton, { width: "60%", maxWidth: 350 }) : subtitle));
};
ContentHeader.Title = Title;

const ContentHeaderSkeleton = ({ hasTitle = true, titleWidth = '35%', hasRole = true, columns, children }) => {
    return React__default.createElement(SkeletonContainer, { hasRole: hasRole },
        React__default.createElement(ContentHeader, { columns: columns },
            hasTitle && React__default.createElement(Skeleton$1, { height: 40, width: titleWidth }),
            children));
};

const headingHeight = 40;
const textHeight = 25;
const DocumentSkeleton = ({ hasRole }) => {
    return React__default.createElement(SkeletonContainer, { hasRole: hasRole },
        React__default.createElement(Skeleton$1, { height: headingHeight, width: "28%", className: className$1('heading') }),
        React__default.createElement(Skeleton$1, { height: textHeight, width: "55%" }),
        React__default.createElement(Skeleton$1, { height: textHeight, width: "45%" }),
        React__default.createElement(Skeleton$1, { height: textHeight, width: "70%" }),
        React__default.createElement(Skeleton$1, { height: textHeight, width: "58%" }),
        React__default.createElement(Skeleton$1, { height: textHeight, width: "65%" }),
        React__default.createElement(Skeleton$1, { height: headingHeight, width: "40%", className: className$1('heading') }),
        React__default.createElement(Skeleton$1, { height: textHeight, width: "50%" }),
        React__default.createElement(Skeleton$1, { height: textHeight, width: "68%" }),
        React__default.createElement(Skeleton$1, { height: textHeight, width: "60%" }));
};
const className$1 = createClassName('DocumentSkeleton');

function useDocumentGroupMatch() {
    var _a, _b, _c;
    const location = useLocation();
    const params = queryString.parse(location.search);
    const vinMatch = matchPath({
        path: '/vehicles/:vin/groups/:groupId'
    }, location.pathname);
    const productMatch = matchPath({
        path: '/products/:productVariantId/groups/:groupId'
    }, location.pathname);
    const productYearMatch = matchPath({
        path: '/products/:productVariantId/:year/groups/:groupId'
    }, location.pathname);
    const paramMatch = typeof params.group === 'string' && isNumeric(params.group) ? params.group : null;
    return (_c = (_b = (_a = vinMatch === null || vinMatch === void 0 ? void 0 : vinMatch.params.groupId) !== null && _a !== void 0 ? _a : productMatch === null || productMatch === void 0 ? void 0 : productMatch.params.groupId) !== null && _b !== void 0 ? _b : productYearMatch === null || productYearMatch === void 0 ? void 0 : productYearMatch.params.groupId) !== null && _c !== void 0 ? _c : paramMatch;
}

const buttonSize = 25;
const DocumentScreenSkeleton = () => {
    const header = React__default.createElement(ContentHeaderSkeleton, { columns: 2, hasRole: false },
        React__default.createElement("div", { className: className('headerButtons') },
            React__default.createElement(Skeleton$1, { variant: "circular", height: buttonSize, width: buttonSize }),
            React__default.createElement(Skeleton$1, { variant: "circular", height: buttonSize, width: buttonSize }),
            React__default.createElement(Skeleton$1, { variant: "circular", height: buttonSize, width: buttonSize })));
    return React__default.createElement(Screen, { header: header },
        React__default.createElement("div", { className: className('content') },
            React__default.createElement(DocumentSkeleton, null)));
};
const className = createClassName('DocumentScreenSkeleton');

export { ContentHeader as C, DocumentScreenSkeleton as D, RouteAccess as R, Screen as S, ContentHeaderSkeleton as a, useModuleDocumentActions as b, useModuleDocumentCoverPages as c, useDocumentGroupMatch as d, useLegacyModuleVehicleTabs as e, useModuleVehicleTabs as f, CentredMessage as g, DocumentSkeleton as h, useModuleSidebarItems as i, useModuleVehicleSidebarItems as j, useModuleAccountMenuItems as k, loadingToken as l, canAccess as m, useModuleHeaderItems as n, useModuleDealerName as o, Centred as p, useModuleAuthRevokeStrategy as q, useModuleLogOut as r, useModuleRelayState as s, useModuleVehicleChanged as t, useModuleSsoConfig as u, useModuleDealerNameAsync as v, useModuleVehicleInformation as w, useModuleDocumentMessages as x };
